/** WILD-03 policy. Payments, SMS, AI and realtime are outside this MVP. */
export type ChatState = 'scheduled' | 'open' | 'closing' | 'closed' | 'archived';
export const CHAT_HOUR = 3_600_000;
export const CHAT_POLL_VISIBLE_MS = 5_000;
export const CHAT_POLL_HIDDEN_MS = 60_000;
export const CHAT_STARTER_CAP = 150;
export function chatMonth(now: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en', { timeZone: timezone, year: 'numeric', month: '2-digit' }).formatToParts(
    now,
  );
  return `${parts.find((p) => p.type === 'year')!.value}-${parts.find((p) => p.type === 'month')!.value}`;
}
export function photoCap(plan: string): number {
  return plan === 'business' ? Number.MAX_SAFE_INTEGER : plan === 'pro' ? 60 : 30;
}
export function chatEndsAt(
  completedAt: string | null,
  cancelledAt: string | null,
  closesAt: string | null,
): number | null {
  const normalEnd = closesAt ? Date.parse(closesAt) : completedAt ? Date.parse(completedAt) + 24 * CHAT_HOUR : null;
  // Terminal transitions may shorten access, never restart archived retention.
  return cancelledAt ? Math.min(Date.parse(cancelledAt), normalEnd ?? Infinity) : normalEnd;
}
export function chatState(
  startsAt: string,
  completedAt: string | null,
  cancelledAt: string | null,
  closesAt: string | null,
  now = Date.now(),
): ChatState {
  const end = chatEndsAt(completedAt, cancelledAt, closesAt);
  if (end !== null) return now >= end + 7 * 24 * CHAT_HOUR ? 'archived' : now >= end ? 'closed' : 'closing';
  return now < Date.parse(startsAt) - 24 * CHAT_HOUR ? 'scheduled' : 'open';
}
export function chatWritable(state: ChatState): boolean {
  return state === 'open' || state === 'closing';
}
export function jobChatEnabled(value: string | undefined, allowlist: string | undefined, companyId: string): boolean {
  return (
    value === undefined ||
    value === 'on' ||
    (value === 'allowlist' &&
      (allowlist ?? '')
        .split(',')
        .map((v) => v.trim())
        .includes(companyId))
  );
}
export const chatCopy = {
  en: {
    title: 'Job chat',
    rating: 'Your rating',
    select: 'Select',
    sample: 'Sample only. No messages are sent.',
    unavailable: 'This chat is not available. Please contact your cleaning company.',
    archived: 'This chat has closed. Please contact your cleaning company.',
    send: 'Send',
    photo: 'Photo',
    feedback: 'Optional private feedback',
    review: 'Share an honest public review',
    saved: 'Saved',
    help: 'About this chat',
    closed: 'This chat is read-only.',
    on_my_way: 'On my way',
    arrived: 'Here',
    running_late: 'Running late',
    done: 'Done',
    opening: 'Your chat for this clean is ready.',
    closing: 'Your clean is complete. You can reply for 24 hours.',
    retry: 'Retry',
    pending: 'Sending',
    open: 'Open',
    scheduled: 'Scheduled',
    closingState: 'Closing',
    closedState: 'Closed',
    archivedState: 'Archived',
  },
  es: {
    title: 'Chat de la visita',
    rating: 'Su valoración',
    select: 'Seleccione',
    sample: 'Solo una muestra. No se envían mensajes.',
    unavailable: 'Este chat no está disponible. Comuníquese con su empresa de limpieza.',
    archived: 'Este chat se ha cerrado. Comuníquese con su empresa de limpieza.',
    send: 'Enviar',
    photo: 'Foto',
    feedback: 'Comentarios privados opcionales',
    review: 'Comparta una reseña pública sincera',
    saved: 'Guardado',
    help: 'Acerca de este chat',
    closed: 'Este chat es de solo lectura.',
    on_my_way: 'Voy en camino',
    arrived: 'Ya llegué',
    running_late: 'Voy con retraso',
    done: 'Terminado',
    opening: 'Su chat para esta limpieza está listo.',
    closing: 'Su limpieza ha terminado. Puede responder durante 24 horas.',
    retry: 'Reintentar',
    pending: 'Enviando',
    open: 'Abierto',
    scheduled: 'Programado',
    closingState: 'Por cerrar',
    closedState: 'Cerrado',
    archivedState: 'Archivado',
  },
} as const;

/** Deliberately closed vocabulary: transport error details and links stay private. */
export function chatNotificationText(
  n: { event_name: string; status: string; reason_code?: string | null },
  es = false,
): string {
  const event =
    n.event_name === 'opened'
      ? es
        ? 'Correo de invitación al chat'
        : 'Chat invitation email'
      : n.event_name === 'closing'
        ? es
          ? 'Correo de finalización de la visita'
          : 'Visit completion email'
        : es
          ? 'Correo de mensajes nuevos'
          : 'New message email';
  if (n.status === 'sent') return `${event}: ${es ? 'enviado.' : 'sent.'}`;
  if (n.status === 'claimed' || n.status === 'queued')
    return `${event}: ${es ? 'envío pendiente de confirmación. No vuelva a enviarlo.' : 'delivery is awaiting confirmation. Do not resend.'}`;
  const reasons: Record<string, [string, string]> = {
    demo: ['The demo does not send email.', 'La demostración no envía correos.'],
    test_recipient: [
      'Demo or test addresses do not receive email.',
      'Las direcciones de prueba o demostración no reciben correos.',
    ],
    delivery_restricted: [
      'Email was intentionally withheld by a sending restriction.',
      'Una restricción de envío ha retenido el correo.',
    ],
    app_unavailable: [
      'Email delivery was unavailable. Retry when service is restored.',
      'El envío no estaba disponible. Reintente cuando se restablezca.',
    ],
    pre_send: [
      'Delivery stopped before sending. Check email settings before retrying.',
      'El envío se detuvo antes de enviar. Revise la configuración antes de reintentar.',
    ],
    access_changed: [
      'Customer access changed. An owner or manager must review it.',
      'Cambió el acceso del cliente. Un propietario o gerente debe revisarlo.',
    ],
  };
  const reason = reasons[n.reason_code ?? ''];
  if (reason) return `${event} ${es ? 'no se envió.' : "wasn't sent."} ${reason[es ? 1 : 0]}`;
  if (n.status === 'suppressed' || n.status === 'unavailable')
    return `${event} ${es ? 'no se envió. Revise el destinatario y la configuración de correo; el registro anterior no incluye el motivo.' : "wasn't sent. Review the recipient and email settings; the older record has no reason."}`;
  return `${event}: ${es ? 'no se pudo confirmar el envío. Revise el registro de correo antes de volver a enviar.' : 'delivery could not be confirmed. Review email history before resending.'}`;
}
